import React from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useMedicationForm } from '@/hooks/useMedicationForm';
import { getStepValidation } from '@/utils/medicationFormValidation';
import MedicationFormSubmitter from '@/components/medication/MedicationFormSubmitter';
import { useFocusFirstInteractiveElement } from '@/hooks/useFocusFirstInteractiveElement';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

// Import components
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import ModalContent from './ModalContent';

// Define the component props interface
interface AddMedicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  editMedication?: any;
  onSuccess?: (medicationData: any) => void;
  insideDialogContent?: boolean;
}

/**
 * AddMedicationModal Component
 * 
 * A dialog for adding or editing medications with a multi-step form
 */
function AddMedicationModal(props: AddMedicationModalProps) {
  // Destructure props
  const { 
    isOpen, 
    onClose,
    editMedication,
    onSuccess,
    insideDialogContent = false 
  } = props;
  
  // Hook declarations (all at the top level)
  const { toast } = useToast();
  const totalSteps = 4;
  const isMobile = useIsMobile();
  
  const {
    formData,
    currentStep,
    updateFormData,
    handleTimeSelection,
    decrementDose,
    removeDoseTime,
    setCurrentStep,
    resetForm
  } = useMedicationForm(editMedication);
  
  const { handleSubmit } = MedicationFormSubmitter({
    formData,
    onSuccess: (medication) => {
      // Event dispatching for medication added
      const event = new CustomEvent('meduler:medication-added', { 
        bubbles: true,
        composed: true,
        detail: { medication, timestamp: Date.now() } 
      });
      window.dispatchEvent(event);
      document.dispatchEvent(new CustomEvent('meduler:medication-added', {
        bubbles: true,
        composed: true,
        detail: { medication, timestamp: Date.now() }
      }));
      
      // Call success handler if provided
      if (onSuccess) {
        onSuccess(medication);
      }
      
      resetForm();
      onClose();
    },
    editMode: !!editMedication,
    editId: editMedication?.id
  });

  // Focus management hook
  useFocusFirstInteractiveElement({
    isOpen,
    currentStep
  });
  
  // Helper functions
  const handleClose = () => {
    resetForm();
    onClose();
  };
  
  const nextStep = () => {
    if (currentStep < totalSteps) {
      if (!getStepValidation(currentStep, formData)) {
        toast({
          title: "Required Fields Missing",
          description: "Please fill in all required fields before continuing.",
          variant: "destructive",
        });
        return;
      }
      setCurrentStep(prev => Math.min(totalSteps, prev + 1));
    }
  };

  // Create the modal content that's used in both scenarios
  const modalContent = (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* CRITICAL: Always include DialogTitle for accessibility */}
      <DialogTitle className="sr-only" id="add-medication-dialog-title">Add Medication</DialogTitle>
      
      {/* Visual title only shown when not inside another dialog */}
      {!insideDialogContent && (
        <div className="text-xl px-4 py-2 font-semibold flex items-center justify-between" aria-hidden="true">
          <span>Add Medication</span>
        </div>
      )}

      {isMobile ? (
        // Mobile layout (vertical) - simplified for smaller screens
        <div className="flex flex-col h-full w-full">
          {/* Mobile Header */}
          <ModalHeader 
            currentStep={currentStep}
            totalSteps={totalSteps}
            formData={formData}
            showSummary={false}
          />
          
          {/* Mobile Content - with proper overflow handling */}
          <div className="flex-1 overflow-y-auto pb-16 min-h-0 w-full"> 
            <ModalContent 
              currentStep={currentStep}
              formData={formData}
              updateFormData={updateFormData}
              handleTimeSelection={handleTimeSelection}
              decrementDose={decrementDose}
              removeDoseTime={removeDoseTime}
              isMobile={true}
            />
          </div>
          
          {/* Mobile Footer - forced to bottom */}
          <ModalFooter 
            currentStep={currentStep}
            totalSteps={totalSteps}
            stepIsValid={getStepValidation(currentStep, formData)}
            onBack={() => setCurrentStep(prev => Math.max(1, prev - 1))}
            onNext={nextStep}
            onSubmit={handleSubmit}
            onCancel={handleClose}
            isMobile={true}
          />
        </div>
      ) : (
        // Desktop layout (horizontal)
        <div className="flex h-full max-h-[90vh]">
          {/* Left side: Header and Navigation */}
          <div className="w-1/3 flex flex-col border-r border-border/20 overflow-y-auto">
            <ModalHeader 
              currentStep={currentStep}
              totalSteps={totalSteps}
              formData={formData}
              showSummary={true}
            />
          </div>
          
          {/* Right side: Form Content and Footer */}
          <div className="w-2/3 flex flex-col">
            <div className="flex-1 overflow-y-auto pb-16 min-h-0"> 
              <ModalContent 
                currentStep={currentStep}
                formData={formData}
                updateFormData={updateFormData}
                handleTimeSelection={handleTimeSelection}
                decrementDose={decrementDose}
                removeDoseTime={removeDoseTime}
              />
            </div>
            
            <ModalFooter 
              currentStep={currentStep}
              totalSteps={totalSteps}
              stepIsValid={getStepValidation(currentStep, formData)}
              onBack={() => setCurrentStep(prev => Math.max(1, prev - 1))}
              onNext={nextStep}
              onSubmit={handleSubmit}
              onCancel={handleClose}
            />
          </div>
        </div>
      )}
    </div>
  );

  // Single return statement using fragment with conditional
  return (
    <>
      {insideDialogContent ? (
        modalContent
      ) : (
        <Dialog open={isOpen} onOpenChange={handleClose}>
          <DialogContent className={cn(
            "p-0 overflow-hidden border-none shadow-xl bg-background/95 backdrop-blur-md",
            "fixed inset-x-0 mx-auto",
            "data-[state=open]:translate-y-0 data-[state=open]:!top-2",
            "data-[state=open]:slide-in-from-top-2",
            "w-[calc(100%-2rem)]",
            "max-w-4xl",
            "max-h-[90vh] flex flex-col",
            "sm:max-h-[80vh] md:max-h-[85vh]",
            "h-auto sm:h-auto"
          )}>
            {modalContent}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// Export the memoized component
export default React.memo(AddMedicationModal);
